import React, { useState, useMemo } from "react";
import { ColumnsType } from "antd/es/table";
import { Tooltip, message, Upload, Button } from "antd";
import {
  InfoCircleOutlined,
  MinusCircleOutlined,
  EyeOutlined,
  MoreOutlined,
  WarningOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import styled from "styled-components";
import Icon from "../../components/Shared/Icon";

dayjs.extend(customParseFormat);

export interface OrderDataType {
  id: number;
  patient_name: string;
  patient_id: string;
  registration_no: string;
  admission_type: string;
  mod_study: string;
  mod_study_id: string;
  scan_time: string;
  status: string;
  status_date: string;
  referring_phy: string;
  referring_phy_type: string;
  hospital: string;
  hospital_id: string;
  grade: string;
  type: string;
  isCritical: boolean;
  isDocumentUploaded: string;
  documentPath?: string;
  aiGraded?: boolean;
}

const OrderColumns = ({
  data,
  activeTab,
  onDocumentUpload,
}: {
  data: OrderDataType[];
  activeTab: string;
  onDocumentUpload?: (recordId: number, file: File) => void;
}): ColumnsType<OrderDataType> => {
  const gradingInfo = (
    <div style={{ maxWidth: '400px', padding: '8px 0' }}>
      <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: 'white', fontSize: '13px' }}>Score 1: The reviewing radiologist agrees with the original report</p>
      <p style={{ margin: '0 0 12px 0', paddingLeft: '12px', fontSize: '12px', color: 'white' }}><strong>1A:</strong> Concur with interpretation</p>
      
      <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: 'white', fontSize: '13px' }}>Score 2: Discrepancy in interpretation—not ordinarily expected to be made</p>
      <p style={{ margin: '0 0 4px 0', paddingLeft: '12px', fontSize: '12px', color: 'white' }}><strong>2A:</strong> Discrepancy that is unlikely to be clinically significant</p>
      <p style={{ margin: '0 0 12px 0', paddingLeft: '12px', fontSize: '12px', color: 'white' }}><strong>2B:</strong> Discrepancy that is likely to be clinically significant</p>
      
      <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: 'white', fontSize: '13px' }}>Score 3: Discrepancy in interpretation—should be made most of the time</p>
      <p style={{ margin: '0 0 4px 0', paddingLeft: '12px', fontSize: '12px', color: 'white' }}><strong>3A:</strong> Discrepancy that is unlikely to be clinically significant</p>
      <p style={{ margin: '0 0 0 0', paddingLeft: '12px', fontSize: '12px', color: 'white' }}><strong>3B:</strong> Discrepancy that is likely to be clinically significant</p>
    </div>
  );

  const getShortGrade = (grade: string) => {
    if (grade.startsWith("1A:")) return "1A";
    if (grade.startsWith("2A:")) return "2A";
    if (grade.startsWith("2B:")) return "2B";
    if (grade.startsWith("3A:")) return "3A";
    if (grade.startsWith("3B:")) return "3B";
    return grade; // Fallback for existing short grades
  };

  const handleClickRow = (record: OrderDataType) => {
    if (record.id) {
      if (record.isDocumentUploaded === "No") {
        message.warning(
          "Document is missing. Please upload the document first."
        );
        return;
      }

      switch (activeTab) {
        case "student_audit":
          window.open(`/cases/auditing/studentReport/${record.id}`, "_blank");
          break;
        case "radiologist_audit":
          window.open(
            `/cases/auditing/radiologistReport/${record.id}`,
            "_blank"
          );
          break;
        case "clinical_correlation":
          window.open(
            `/cases/auditing/clinicalCorrelation/${record.id}`,
            "_blank"
          );
          break;
        default:
          window.open(`/cases/auditing/auditReport/${record.id}`, "_blank");
          break;
      }
    }
  };

  return [
    {
      title: "PATIENT NAME",
      align: "left" as const,
      dataIndex: "patient_name",
      key: "patient_name",
      sorter: true,
      width: "14%",
      render: (text: string, record: OrderDataType) => (
        <div
          style={{
            textAlign: "left",
            padding: "0.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            height: "100%",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              fontWeight: "600",
              color: "#1976d2",
              fontSize: "0.9rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {record.patient_name}
            {record.isCritical ? (
              <Icon name="critical" width={15} height={15} />
            ) : null}
          </div>
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                fontSize: "0.75rem",
                color: "#4a5568",
                backgroundColor: "#edf2f7",
                padding: "0.1rem 0.6rem",
                
                borderRadius: "8px",
                whiteSpace: "nowrap",
                border: "1px solid #e2e8f0",
                fontWeight: "500",
              }}
            >
              {record.patient_id}
            </div>
            <div
              style={{
                fontSize: "0.75rem",
                color: "#4a5568",
                backgroundColor: "#edf2f7",
                padding: "0.1rem 0.6rem",
                borderRadius: "8px",
                whiteSpace: "nowrap",
                border: "1px solid #e2e8f0",
                fontWeight: "500",
              }}
            >
              {record.registration_no}
            </div>
            <div
              style={{
                fontSize: "0.75rem",
                color: "#4a5568",
                backgroundColor: "#edf2f7",
                padding: "0.1rem 0.6rem",
                borderRadius: "8px",
                whiteSpace: "nowrap",
                border: "1px solid #e2e8f0",
                fontWeight: "500",
              }}
            >
              {record.admission_type}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "MOD STUDY",
      align: "left" as const,
      dataIndex: "mod_study",
      width: "14%",
      render: (text: string, record: OrderDataType) => {
        return (
          <div
            style={{
              textAlign: "left",
              padding: "0.5rem",
              height: "100%",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                fontSize: "0.875rem",
                fontWeight: "500",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {record.mod_study}
            </div>
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                marginTop: "0.25rem",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#4a5568",
                  backgroundColor: "#edf2f7",
                  padding: "0.1rem 0.6rem",
                  borderRadius: "8px",
                  whiteSpace: "nowrap",
                  border: "1px solid #e2e8f0",
                  fontWeight: "500",
                }}
              >
                {record.mod_study_id}
              </div>
            </div>
          </div>
        );
      },
    },
    // {
    //   title: "SCAN TIME",
    //   align: "center",
    //   dataIndex: "scan_time",
    //   width: "11%",
    //   render: (text: string) => {
    //     const randomDays = Math.floor(Math.random() * 730);
    //     const randomDate = dayjs().subtract(randomDays, "day");
    //     const randomHours = Math.floor(Math.random() * 12) + 1;
    //     const randomMinutes = Math.floor(Math.random() * 60);
    //     const ampm = Math.random() > 0.5 ? "AM" : "PM";

    //     return (
    //       <div
    //         style={{
    //           display: "flex",
    //           flexDirection: "column",
    //           alignItems: "flex-start",
    //           padding: "0.5rem",
    //           height: "100%",
    //           boxSizing: "border-box",
    //         }}
    //       >
    //         <div
    //           style={{
    //             fontSize: "0.75rem",
    //             color: "#666",
    //             lineHeight: "1.2",
    //             whiteSpace: "nowrap",
    //           }}
    //         >
    //           {randomDate.format("DD MMM YYYY")},
    //         </div>
    //         <div
    //           style={{
    //             fontSize: "0.75rem",
    //             color: "#666",
    //             lineHeight: "1.2",
    //             whiteSpace: "nowrap",
    //           }}
    //         >
    //           {randomHours.toString().padStart(2, "0")}:
    //           {randomMinutes.toString().padStart(2, "0")} {ampm}
    //         </div>
    //       </div>
    //     );
    //   },
    //   sorter: true,
    // },
    {
      title: "STATUS",
      align: "left" as const,
      dataIndex: "status",
      width: "11%",
      render: (text: string, record: OrderDataType) => {
        const statusDate = dayjs(record.status_date);
        const statusRandomHours = Math.floor(Math.random() * 12) + 1;
        const statusRandomMinutes = Math.floor(Math.random() * 60);
        const statusAmpm = Math.random() > 0.5 ? "AM" : "PM";

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              padding: "0.5rem",
              height: "100%",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                fontSize: "0.8rem",
                color: "#008000",
                
                backgroundColor: "#e1fcef",
                padding: "0.2rem 0.7rem",
                borderRadius: "8px",
                marginBottom: "0.25rem",
                whiteSpace: "nowrap",
                fontWeight: "500",
                
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
              }}
            >
              {record.status}
            </div>
            <div
              style={{
                fontSize: "0.75rem",
                color: "#666",
                lineHeight: "1.2",
                whiteSpace: "nowrap",
              }}
            >
              {statusDate.format("DD MMM YYYY")},{" "}
              {statusRandomHours.toString().padStart(2, "0")}:
              {statusRandomMinutes.toString().padStart(2, "0")} {statusAmpm}
            </div>
          </div>
        );
      },
    },
    {
      title: "REFERRING PHY",
      align: "left" as const,
      dataIndex: "referring_phy",
      width: "14%",
      render: (text: string, record: OrderDataType) => {
        return (
          <div
            style={{
              textAlign: "left",
              padding: "0.5rem",
              height: "100%",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                fontSize: "0.875rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {record.referring_phy}
            </div>
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                marginTop: "0.25rem",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#4a5568",
                  backgroundColor: "#edf2f7",
                  padding: "0.1rem 0.6rem",
                  borderRadius: "8px",
                  whiteSpace: "nowrap",
                  border: "1px solid #e2e8f0",
                  fontWeight: "500",
                }}
              >
                {record.referring_phy_type}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "HOSPITAL",
      align: "left" as const,
      dataIndex: "hospital",
      width: "11%",
      render: (text: string, record: OrderDataType) => {
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              padding: "0.5rem",
              height: "100%",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                fontSize: "0.9rem",
                fontWeight: "500",
                color: "#4a5568",
                lineHeight: "1.2",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {record.hospital}
            </div>
            <div
              style={{
                fontSize: "0.75rem",
                color: "#4a5568",
                backgroundColor: "#edf2f7",
                padding: "0.1rem 0.6rem",
                borderRadius: "8px",
                marginTop: "0.25rem",
                whiteSpace: "nowrap",
                border: "1px solid #e2e8f0",
                fontWeight: "500",
              }}
            >
              {record.hospital_id}
            </div>
          </div>
        );
      },
    },
    ...(activeTab === "clinical_correlation"
      ? [
          {
            title: "HIS/EMR",
            align: "left" as const,
            width: "10%",
            render: (text: string, record: OrderDataType) => (
              <div 
                style={{ 
                  textAlign: "left",
                  padding: "0.5rem",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  height: "100%",
                  width: "100%"
                }}
              >
                {record.isDocumentUploaded === "Yes" ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "0.4rem",
                      padding: "0.2rem 0.5rem",
                      backgroundColor: "#f0f9ff",
                      borderRadius: "16px",
                      border: "1px solid #bae6fd",
                      width: "100px",
                      height: "24px"
                    }}
                  >
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        backgroundColor: "#0ea5e9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        flexShrink: 0
                      }}
                    >
                      <svg width="6" height="6" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                    </div>
                    <div
                      style={{
                        fontSize: "0.65rem",
                        fontWeight: "500",
                        color: "#0c4a6e",
                        textAlign: "left",
                        flex: 1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                    >
                      Uploaded
                    </div>
                  </div>
                ) : (
                  <Upload
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      if (onDocumentUpload) {
                        onDocumentUpload(record.id, file);
                      }
                      return false; // Prevent automatic upload
                    }}
                    disabled={false}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "0.4rem",
                        padding: "0.2rem 0.5rem",
                        backgroundColor: "#fffbeb",
                        borderRadius: "16px",
                        border: "1px solid #fed7aa",
                        width: "100px",
                        height: "24px",
                        cursor: "pointer",
                        transition: "all 0.2s ease"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#fef3c7";
                        e.currentTarget.style.borderColor = "#fbbf24";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#fffbeb";
                        e.currentTarget.style.borderColor = "#fed7aa";
                      }}
                    >
                      <div
                        style={{
                          width: "12px",
                          height: "12px",
                          borderRadius: "50%",
                          backgroundColor: "#f59e0b",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          flexShrink: 0
                        }}
                      >
                        <UploadOutlined style={{ fontSize: "6px" }} />
                      </div>
                      <div
                        style={{
                          fontSize: "0.65rem",
                          fontWeight: "500",
                          color: "#92400e",
                          textAlign: "left",
                          flex: 1,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                      >
                        Upload
                      </div>
                    </div>
                  </Upload>
                )}
              </div>
            ),
          },
        ]
      : []),
    {
      title: activeTab === "clinical_correlation" ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-start' }}>
          <span>CORRELATION STATUS</span>
          <Tooltip title="Clinical correlation status indicates whether the radiological findings have been correlated with clinical outcomes">
            <InfoCircleOutlined style={{ color: '#6366f1', cursor: 'pointer', fontSize: '14px' }} />
          </Tooltip>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>GRADE</span>
          <Tooltip title={gradingInfo}>
            <InfoCircleOutlined style={{ color: '#9370DB', cursor: 'pointer' }} />
          </Tooltip>
        </div>
      ),
      align: "left" as const,
      dataIndex: "grade",
      width: "12%",
      render: (text: string, record: OrderDataType, index: number) => {
        const isPending = text === "-";
        return activeTab === "clinical_correlation" ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
              height: "100%",
              padding: "0.5rem",
              boxSizing: "border-box",
              width: "100%",
            }}
          >
            {record.grade === "Yes" ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "0.4rem",
                  padding: "0.2rem 0.5rem",
                  backgroundColor: "#f0fdf4",
                  borderRadius: "16px",
                  border: "1px solid #bbf7d0",
                  width: "100px",
                  height: "24px"
                }}
              >
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor: "#22c55e",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    flexShrink: 0
                  }}
                >
                  <svg width="6" height="6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </div>
                <div
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: "500",
                    color: "#166534",
                    textAlign: "left",
                    flex: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  Correlated
                </div>
              </div>
            ) : record.grade === "No" ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "0.4rem",
                  padding: "0.2rem 0.5rem",
                  backgroundColor: "#fef2f2",
                  borderRadius: "16px",
                  border: "1px solid #fecaca",
                  width: "100px",
                  height: "24px"
                }}
              >
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor: "#ef4444",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    flexShrink: 0
                  }}
                >
                  <svg width="6" height="6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </div>
                <div
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: "500",
                    color: "#991b1b",
                    textAlign: "left",
                    flex: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  Mismatch
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "0.4rem",
                  padding: "0.2rem 0.5rem",
                  backgroundColor: "#f8fafc",
                  borderRadius: "16px",
                  border: "1px solid #e2e8f0",
                  width: "100px",
                  height: "24px"
                }}
              >
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor: "#94a3b8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    flexShrink: 0
                  }}
                >
                  <svg width="6" height="6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                <div
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: "500",
                    color: "#475569",
                    textAlign: "left",
                    flex: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  Pending
                </div>
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
              height: "100%",
              padding: "0.5rem",
              boxSizing: "border-box",
              gap: "0.25rem",
              minWidth: "80px",
              maxWidth: "80px",
              position: "relative",
              overflow: "visible",
            }}
          >
            <Tooltip title={record.aiGraded ? "AI Graded" : "QA Graded"}>
              <div
                style={{
                  fontSize: "0.8rem",
                  fontWeight: "500",
                  color: "#4a5568",
                  backgroundColor: "#edf2f7",
                  padding: "0.1rem 0.6rem",
                  borderRadius: "8px",
                  whiteSpace: "nowrap",
                  textAlign: "center",
                  border: "1px solid #e2e8f0",
                  width: "50px",
                  cursor: "pointer",
                  position: "relative",
                  animation: record.aiGraded
                    ? "aiRotateGlow 3s linear infinite"
                    : "none",
                  boxShadow: record.aiGraded
                    ? "0 0 12px rgba(168, 85, 247, 0.6)"
                    : "none",
                }}
              >
                {getShortGrade(record.grade)}
                {record.aiGraded && (
                  <img
                    src="/icons/ai.svg"
                    alt="AI Graded"
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                      position: "absolute",
                      top: "-4px",
                      right: "-4px",
                      zIndex: 10,
                    }}
                  />
                )}
              </div>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "ACTION",
      align: "center" as const,
      key: "action",
      width: "5%",
      render: (_, record) => (
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            padding: "0.5rem",
            boxSizing: "border-box",
          }}
        >
          <Tooltip
            placement="top"
            title={
              record.isDocumentUploaded === "No"
                ? "Document is missing"
                : "View Case"
            }
            trigger="hover"
          >
            <span
              style={{
                cursor:
                  record.isDocumentUploaded === "No"
                    ? "not-allowed"
                    : "pointer",
                opacity: record.isDocumentUploaded === "No" ? 0.5 : 1,
                pointerEvents:
                  record.isDocumentUploaded === "No" ? "none" : "auto",
              }}
            >
              <span style={{ color: "red", fill: "red" }}>
                <div
                  style={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    backgroundColor: "#9370DB",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    record.isDocumentUploaded !== "No" && handleClickRow(record)
                  }
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="white"
                    style={{ marginLeft: "1px" }}
                  >
                    <path d="M9.707 18.707l6-6a.999.999 0 000-1.414l-6-6a.999.999 0 10-1.414 1.414L13.586 12l-5.293 5.293a.999.999 0 101.414 1.414z" />
                  </svg>
                </div>
              </span>
            </span>
          </Tooltip>
        </div>
      ),
    },
  ];
};

export default OrderColumns;

const StyledViewIcon = styled(Icon)`
  cursor: pointer;
  color: red;
  fill: red;
`;

// Add CSS animation for AI glow effect
const style = document.createElement('style');
style.textContent = `
  @keyframes aiRotateGlow {
    0% {
      box-shadow: 0 0 4px rgba(168, 85, 247, 0.3), 0 0 2px rgba(59, 130, 246, 0.2);
    }
    50% {
      box-shadow: 0 0 8px rgba(168, 85, 247, 0.5), 0 0 4px rgba(59, 130, 246, 0.3);
    }
    100% {
      box-shadow: 0 0 4px rgba(168, 85, 247, 0.3), 0 0 2px rgba(59, 130, 246, 0.2);
    }
  }
`;
document.head.appendChild(style);
